<template>
  <div>
    <fieldset>
      <div class="field">
        <label class="label">App Name</label>
        <div class="control">
          <input
            class="input"
            type="text"
            placeholder="Enter app name here"
            required
            v-model.trim="app.name"
            @input="$v.app.name.$touch()"
            :class="{ 'is-danger': $v.app.name.$invalid && $v.app.name.$dirty }"
          />
          <p class="help is-danger" v-if="$v.app.name.$invalid && $v.app.name.$dirty">The app name is required</p>
        </div>
      </div>

      <div class="field">
        <label class="label">Capabilities</label>
        <div class="control">
          <label class="checkbox">
            <input type="checkbox" v-model="app.capabilities.supportsBotAction" />
            Supports bot action
          </label>
        </div>
        <div class="control">
          <label class="checkbox">
            <input type="checkbox" v-model="app.capabilities.supportsHomescreen" />
            Supports homescreen
          </label>
        </div>
        <div class="control">
          <label class="checkbox">
            <input type="checkbox" v-model="app.capabilities.supportsWebhooks" />
            Supports webhooks
          </label>
        </div>
      </div>

      <div class="field">
        <label class="label">Config URL</label>
        <div class="control">
          <input
            class="input"
            type="text"
            placeholder="Enter config URL here"
            required
            v-model.trim="app.workflowOptions.configUrl"
            @input="$v.app.workflowOptions.configUrl.$touch()"
            :class="{
              'is-danger': $v.app.workflowOptions.configUrl.$invalid && $v.app.workflowOptions.configUrl.$dirty,
            }"
            :disabled="!supportsAppLifecycleHooks()"
          />
          <p
            class="help is-danger"
            v-if="$v.app.workflowOptions.configUrl.$invalid && $v.app.workflowOptions.configUrl.$dirty"
          >
            Must be a valid URL
          </p>
        </div>
      </div>

      <div class="field">
        <label class="label">Init URL</label>
        <div class="control">
          <input
            class="input"
            type="text"
            placeholder="Enter init URL here"
            required
            v-model.trim="app.workflowOptions.initUrl"
            @input="$v.app.workflowOptions.initUrl.$touch()"
            :class="{ 'is-danger': $v.app.workflowOptions.initUrl.$invalid && $v.app.workflowOptions.initUrl.$dirty }"
            :disabled="!supportsAppLifecycleHooks()"
          />
          <p
            class="help is-danger"
            v-if="$v.app.workflowOptions.initUrl.$invalid && $v.app.workflowOptions.initUrl.$dirty"
          >
            Must be a valid URL
          </p>
        </div>
      </div>

      <div class="field">
        <label class="label">Submit URL</label>
        <div class="control">
          <input
            class="input"
            type="text"
            placeholder="Enter submit URL here"
            required
            v-model.trim="app.workflowOptions.submitUrl"
            @input="$v.app.workflowOptions.submitUrl.$touch()"
            :class="{
              'is-danger': $v.app.workflowOptions.submitUrl.$invalid && $v.app.workflowOptions.submitUrl.$dirty,
            }"
            :disabled="!supportsAppLifecycleHooks()"
          />
          <p
            class="help is-danger"
            v-if="$v.app.workflowOptions.submitUrl.$invalid && $v.app.workflowOptions.submitUrl.$dirty"
          >
            Must be a valid URL
          </p>
        </div>
      </div>

      <div class="field">
        <label class="label">Webhook URL</label>
        <div class="control">
          <input
            class="input"
            type="text"
            placeholder="Enter webhook URL here"
            required
            v-model.trim="app.workflowOptions.webhookUrl"
            @input="$v.app.workflowOptions.webhookUrl.$touch()"
            :class="{
              'is-danger': $v.app.workflowOptions.webhookUrl.$invalid && $v.app.workflowOptions.webhookUrl.$dirty,
            }"
            :disabled="!app.capabilities.supportsWebhooks"
          />
          <p
            class="help is-danger"
            v-if="$v.app.workflowOptions.webhookUrl.$invalid && $v.app.workflowOptions.webhookUrl.$dirty"
          >
            Must be a valid URL
          </p>
        </div>
      </div>

      <div class="field">
        <label class="label">Verification Token</label>
        <div class="control">
          <div class="field has-addons">
            <div class="control is-expanded">
              <input class="input" type="text" disabled v-model="app.workflowOptions.verificationToken" />
            </div>
            <p class="control">
              <button class="button is-info" @click="copyToClipboard(app.workflowOptions.verificationToken)">
                <span class="icon">
                  <font-awesome-icon icon="copy"></font-awesome-icon>
                </span>
              </button>
            </p>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">API Token</label>
        <div class="control">
          <div class="field has-addons">
            <div class="control is-expanded">
              <input class="input" type="text" disabled v-model="app.apiToken" />
            </div>
            <p class="control">
              <button class="button is-info" @click="copyToClipboard(app.apiToken)">
                <span class="icon">
                  <font-awesome-icon icon="copy"></font-awesome-icon>
                </span>
              </button>
            </p>
          </div>
        </div>
      </div>
      <n-button-group>
        <n-button variant="primary" @click="save()" :disabled="$v.$invalid" :loading="actionInProgress"
          >Submit</n-button
        >
        <n-button @click="cancel()" variant="primary--outline">Cancel</n-button>
      </n-button-group>
    </fieldset>
  </div>
</template>

<script lang="ts" setup>
import { required } from '@vuelidate/validators';
import { appsApi } from '@/features/apps/shared/services/apps';
import { PrivateApp, UpdatePrivateAppViewModel } from '@/features/apps/shared/models';
import Toast from '@/shared/services/toast';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { useClipboard } from '@vueuse/core';

const url = (value: string) => {
  if (!value) {
    return true;
  }
  return value.startsWith('http://') || value.startsWith('https://');
};

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);
const route = useRoute();
const router = useRouter();

const actionInProgress = ref(false);
const app = ref<PrivateApp>({
  id: '',
  name: '',
  ownerWorkspaceId: '',
  apiToken: '',
  capabilities: {
    supportsWebhooks: false,
    supportsBotAction: false,
    supportsHomescreen: false,
  },
  workflowOptions: {
    configUrl: '',
    initUrl: '',
    submitUrl: '',
    webhookUrl: '',
    verificationToken: '',
    webhookTopics: [],
  },
});

const validations = {
  app: {
    name: { required },
    workflowOptions: {
      configUrl: { url },
      initUrl: { url },
      submitUrl: { url },
      webhookUrl: { url },
    },
  },
};

const $v = useVuelidate(validations, { app });

const emit = defineEmits<{
  (e: 'loading'): void;
  (e: 'loaded'): void;
}>();

function supportsAppLifecycleHooks() {
  return app.value.capabilities.supportsHomescreen || app.value.capabilities.supportsBotAction;
}

onBeforeMount(() => {
  emit('loading');
  if (!currentWorkspace.value) {
    handleSeriousError('No workspace selected');
    return;
  }

  appsApi
    .getPrivateApp(currentWorkspace.value.id, <string>route.params.id)
    .then((fetchedApp) => {
      app.value = fetchedApp;
    })
    .catch((err) => {
      handleSeriousError(err.message);
    })
    .finally(() => emit('loaded'));
});

const { copy, copied } = useClipboard();

const copyToClipboard = async (toCopyText: string) => {
  try {
    await copy(toCopyText);
    if (copied) {
      Toast.showInfo('Copied to clipboard');
    }
  } catch (err) {
    console.error('Failed to copy text: ', err);
  }
};

function save() {
  if (actionInProgress.value) {
    return;
  }

  actionInProgress.value = true;

  const request = {
    appName: app.value.name,
    capabilities: app.value.capabilities,
    workflowOptions: app.value.workflowOptions,
  } as UpdatePrivateAppViewModel;

  appsApi
    .updatePrivateApp(currentWorkspace.value.id, app.value.id, request)
    .then(() => {
      Toast.showSuccess('App config saved');
      router.push({ name: 'private-apps-list' });
    })
    .catch((err) => Toast.showError(err.message))
    .finally(() => (actionInProgress.value = false));
}

function cancel() {
  router.push({ name: 'private-apps-list' });
}
</script>
